
import { useEffect, lazy, Suspense, useState, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import Hero from "@/components/Hero";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WelcomeBackBanner from "@/components/WelcomeBackBanner";
import SecurityFooter from "@/components/SecurityFooter";
import StickyCtaBar from "@/components/StickyCtaBar";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import DiasporaSection from "@/components/DiasporaSection";
import { useSecureSession } from "@/hooks/useSecureSession";
import { Skeleton } from "@/components/ui/skeleton";

// Lazy load non-critical components with explicit chunk names
const HowItWorks = lazy(() => import(/* webpackChunkName: "how-it-works" */ "@/components/HowItWorks"));
const LiveDemoPreview = lazy(() => import(/* webpackChunkName: "live-demo" */ "@/components/LiveDemoPreview"));
const MarketplacePreview = lazy(() => import(/* webpackChunkName: "marketplace" */ "@/components/MarketplacePreview"));
const AgentVerification = lazy(() => import(/* webpackChunkName: "agent-verification" */ "@/components/AgentVerification"));
const ImpactNumbers = lazy(() => import(/* webpackChunkName: "impact-numbers" */ "@/components/ImpactNumbers"));
const BlogPreview = lazy(() => import(/* webpackChunkName: "blog-preview" */ "@/components/BlogPreview"));
const TestimonialCarousel = lazy(() => import(/* webpackChunkName: "testimonials" */ "@/components/TestimonialCarousel"));
const SecurityTrust = lazy(() => import(/* webpackChunkName: "security" */ "@/components/SecurityTrust"));
const CTA = lazy(() => import(/* webpackChunkName: "cta" */ "@/components/CTA"));

// Loading component optimized for lazy loaded sections
const SectionLoader = () => (
  <div className="py-12 sm:py-16">
    <Skeleton className="h-8 w-48 sm:h-10 sm:w-64 mx-auto mb-4 sm:mb-6" />
    <Skeleton className="h-4 w-72 sm:h-5 sm:w-80 mx-auto mb-8 sm:mb-10" />
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto px-4">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="space-y-2 sm:space-y-4">
          <Skeleton className="h-32 sm:h-40 w-full" />
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-full" />
        </div>
      ))}
    </div>
  </div>
);

// Component for intersection observer based lazy loading
const LazySection = ({ children, id = "", className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id={id} ref={sectionRef} className={className}>
      {isVisible ? children : <SectionLoader />}
    </section>
  );
};

const Index = () => {
  const { user, loading } = useAuth();
  useSecureSession(); // Enable session management

  useEffect(() => {
    // Add canonical tag for SEO
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://zimkonnect.com/');
    } else {
      const newCanonical = document.createElement('link');
      newCanonical.rel = 'canonical';
      newCanonical.href = 'https://zimkonnect.com/';
      document.head.appendChild(newCanonical);
    }
    
    // Set page metadata for SEO
    document.title = "ZimKonnect | Track Building Projects in Zimbabwe from Anywhere";
  }, []);

  // Show compact loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 sm:h-20 sm:w-20 border-b-2 border-amber-500 mx-auto mb-4"></div>
          <p className="text-amber-800">Loading ZimKonnect...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Header />
      {user && <WelcomeBackBanner user={user} />}
      <Hero />
      
      {/* Add DiasporaSection after Hero */}
      <DiasporaSection />
      
      <Suspense fallback={<SectionLoader />}>
        <LazySection id="how-it-works">
          <HowItWorks />
        </LazySection>
      </Suspense>
      
      <Suspense fallback={<SectionLoader />}>
        <LazySection>
          <LiveDemoPreview />
        </LazySection>
      </Suspense>
      
      <Suspense fallback={<SectionLoader />}>
        <LazySection>
          <MarketplacePreview />
        </LazySection>
      </Suspense>
      
      <Suspense fallback={<SectionLoader />}>
        <LazySection>
          <AgentVerification />
        </LazySection>
      </Suspense>
      
      <Suspense fallback={<SectionLoader />}>
        <LazySection>
          <ImpactNumbers />
        </LazySection>
      </Suspense>
      
      <Suspense fallback={<SectionLoader />}>
        <LazySection>
          <BlogPreview />
        </LazySection>
      </Suspense>
      
      <Suspense fallback={<SectionLoader />}>
        <LazySection>
          <TestimonialCarousel />
        </LazySection>
      </Suspense>
      
      <Suspense fallback={<SectionLoader />}>
        <LazySection>
          <SecurityTrust />
        </LazySection>
      </Suspense>
      
      <Suspense fallback={<SectionLoader />}>
        <LazySection id="join-waitlist">
          <CTA />
        </LazySection>
      </Suspense>
      
      <Footer />
      <SecurityFooter />
      <StickyCtaBar />
      <CookieConsentBanner />
    </div>
  );
};

export default Index;
